import React from 'react';
import {
  Formik,
  Field,
} from 'formik';
import {
  injectStripe,
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from 'react-stripe-elements';
import * as Yup from 'yup';

import {
  Button,
  TextField,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';

import * as S from './styled';

type Props = {
  stripe: any,
  elements: any,
  submitText: string,
  onCancel: { (): void },
  onSuccess: { (response: any, action: any): void },
};

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Cardholder name is required'),
  cardNumberIsValid: Yup.mixed()
    .oneOf([true, null], 'Please enter a valid card number')
    .required('This field is required'),
  cardExpiryIsValid: Yup.mixed()
    .oneOf([true, null], 'Please enter a valid card expiration date')
    .required('This field is required'),
  CVCIsValid: Yup.mixed()
    .oneOf([true, null], 'Please enter a valid CVC')
    .required('This field is required'),
});

const AddCardForm = ({
  stripe,
  elements,
  submitText,
  onCancel,
  onSuccess,
}: Props) => {
  const onSubmit = async ({ name }, actions) => {
    if (stripe) {
      const res = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement('cardNumber'),
        billing_details: {
          name,
        },
      });

      if (res.error) {
        console.log(res.error);
      } else {
        onSuccess(res, actions);
      }
    } else {
      console.log("Stripe.js hasn't loaded yet.");
    }
  };

  return (
    <Flex width="100%">
      <Formik
        initialValues={{
          name: '',
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({
          errors,
          values,
          setFieldValue,
          isSubmitting,
          handleSubmit,
        }) => (
          <S.AddCardForm
            onSubmit={async (e) => {
              e.persist();
              e.preventDefault();
              await ['cardNumberIsValid', 'cardExpiryIsValid', 'CVCIsValid']
                .forEach((item) => {
                  if (!Object.keys(values).includes(item)) {
                    setFieldValue(item, null);
                  }
                });
              handleSubmit(e);
            }}
          >
            <S.CardNumContainer
              hasError={!!errors.cardNumberIsValid && Object.keys(values).includes('cardNumberIsValid')}
            >
              <Text
                size="13px"
                lineHeight="18px"
                weight="bold"
              >
                Card Number *
              </Text>
              <CardNumberElement
                className="card-element"
                onChange={async (e) => {
                  await setFieldValue('cardNumberIsValid', e.complete);
                }}
              />
              <Text
                className="error-message"
              >
                {errors.cardNumberIsValid}
              </Text>
            </S.CardNumContainer>

            <Flex
              width="100%"
              justify="space-between"
            >
              <S.ExpirationDate
                hasError={!!errors.cardExpiryIsValid && typeof values.cardExpiryIsValid !== 'undefined'}
              >
                <Text
                  size="13px"
                  lineHeight="18px"
                  weight="bold"
                >
                  Expiration Date *
                </Text>
                <CardExpiryElement
                  className="card-element"
                  onChange={e => setFieldValue('cardExpiryIsValid', e.complete)}
                />
                <Text
                  className="error-message"
                >
                  {errors.cardExpiryIsValid}
                </Text>
              </S.ExpirationDate>
              <S.CVVContainer
                hasError={!!errors.CVCIsValid && typeof values.CVCIsValid !== 'undefined'}
              >
                <Text
                  size="13px"
                  lineHeight="18px"
                  weight="bold"
                >
                  CVC *
                </Text>
                <CardCvcElement
                  className="card-element"
                  onChange={e => setFieldValue('CVCIsValid', e.complete)}
                />
                <Text
                  className="error-message"
                >
                  {errors.CVCIsValid}
                </Text>
              </S.CVVContainer>
            </Flex>
            <Field
              name="name"
              color="#333"
              label="Card Holder Name *"
              spacing={5}
              height="38px"
              component={TextField}
              placeholder="Enter Name"
              autoComplete="off"
              labelWeight="bold"
            />
            <Flex
              width="100%"
              align="center"
              justify="space-between"
            >
              <Flex
                align="center"
                spacing={10}
              >
                <S.LockIcon />
                <Text
                  lineHeight="1.1em"
                >
                  This is a secure 128-bit SSL
                  <br />
                  encrypted payment.
                </Text>
              </Flex>
              <Flex
                spacing={10}
              >
                <Button
                  ghost
                  type="button"
                  appearance="danger"
                  onClick={onCancel}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  appearance="primary"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                >
                  {submitText || 'Add'}
                </Button>
              </Flex>
            </Flex>
          </S.AddCardForm>
        )}
      </Formik>
    </Flex>
  );
};

export default injectStripe(AddCardForm);
